import React, { useEffect, useState, useMemo } from "react";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { Link } from "react-router-dom";
import Table from "../../core/pagination/datatable.jsx";
import TooltipIcons from "../../core/common/tooltip-content/tooltipIcons";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
import useStore from "../../store/store.js";
import EmployeeServices from "../../services/EmployeeServices.js";
import useAsync from "../../hooks/useAsync.js";
import useAttendance from "../../hooks/useAttendance.js";
import PredefinedDateRanges from "../../core/common/range-picker/datePicker.jsx";
import moment from "moment";
import CommonFooter from "../../core/common/footer/commonFooter.jsx";

const AttendanceEmployee = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [attendanceData, setAttendanceData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [todayStatus, setTodayStatus] = useState(null);
  const [employeeFilter, setEmployeeFilter] = useState("all");
  const [dateRange, setDateRange] = useState({
    startDate: moment().startOf("month").toDate(),
    endDate: moment().endOf("month").toDate(),
  });
  const [selectedAction, setSelectedAction] = useState(""); // For bulk action dropdown

  const selectedStore = useStore((state) => state.selectedStore);
  const storeId = selectedStore?._id;

  const {
    data: employees,
    loading: employeesLoading,
    // refetch: refetchEmployees,
  } = useAsync(() => {
    const fetchEmployees = async () => {
      if (!storeId) {
        return [];
      }
      const response = await EmployeeServices.getEmployees(storeId);
      return response;
    };
    return fetchEmployees();
  }, [storeId]);

  const {
    clockIn,
    clockOut,
    getTodayStatus,
    getAllEmployeesAttendance,
    createManualAttendance,
    loading: attendanceLoading,
  } = useAttendance();

  // Set first employee as default when employees load
  useEffect(() => {
    if (employees && employees.length > 0 && !selectedEmployee) {
      setSelectedEmployee(employees[0]._id);
    }
  }, [employees, selectedEmployee]);

  // Fetch attendance data when date range changes
  useEffect(() => {
    const fetchAttendance = async () => {
      if (storeId && dateRange.startDate && dateRange.endDate) {
        try {
          const params = {
            startDate: new Date(dateRange.startDate).toISOString(),
            endDate: new Date(dateRange.endDate).toISOString(),
          };

          const response = await getAllEmployeesAttendance(params, storeId);

          setAttendanceData(response.attendance || []);
        } catch (error) {
          console.error("Error fetching attendance:", error);
          setAttendanceData([]);
        }
      }
    };

    fetchAttendance();
  }, [dateRange, getAllEmployeesAttendance, storeId]);

  // Calculate summary for selected employee
  useEffect(() => {
    if (selectedEmployee && attendanceData.length > 0) {
      const employeeAttendance = attendanceData.filter(
        (record) => record.employee?._id === selectedEmployee
      );

      const calculatedSummary = {
        totalDays: employeeAttendance.length,
        present: employeeAttendance.filter((a) => a.status === "Present")
          .length,
        absent: employeeAttendance.filter((a) => a.status === "Absent").length,
        holidays: employeeAttendance.filter((a) => a.status === "Holiday")
          .length,
        halfDays: employeeAttendance.filter((a) => a.status === "Half Day")
          .length,
        totalHours: employeeAttendance.reduce(
          (sum, a) => sum + (a.totalHours || 0),
          0
        ),
        totalOvertime: employeeAttendance.reduce(
          (sum, a) => sum + (a.overTime || 0),
          0
        ),
      };

      setSummary(calculatedSummary);
    } else {
      setSummary(null);
    }
  }, [selectedEmployee, attendanceData]);

  // Fetch today's status
  useEffect(() => {
    const fetchTodayStatus = async () => {
      if (selectedEmployee && storeId) {
        try {
          const response = await getTodayStatus(selectedEmployee);
          setTodayStatus(response);
        } catch (error) {
          console.error("Error fetching today status:", error);
        }
      }
    };

    fetchTodayStatus();
  }, [selectedEmployee, storeId, getTodayStatus]);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) => {
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  };

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const getTotalDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  // Handle employee selection
  const handleEmployeeChange = (e) => {
    setSelectedEmployee(e.target.value);
  };

  // Handle date range change from date picker
  const handleDateRangeChange = (range) => {
    setDateRange({
      startDate: range.startDate,
      endDate: range.endDate,
    });
  };

  // Refresh attendance data
  const refreshAttendance = async () => {
    if (storeId && dateRange.startDate && dateRange.endDate) {
      try {
        const params = {
          startDate: new Date(dateRange.startDate).toISOString(),
          endDate: new Date(dateRange.endDate).toISOString(),
        };

        const response = await getAllEmployeesAttendance(params, storeId);
        setAttendanceData(response.attendance || []);

        // Also refresh today's status for selected employee if any
        if (selectedEmployee) {
          const statusResponse = await getTodayStatus(selectedEmployee);
          setTodayStatus(statusResponse);
        }
      } catch (error) {
        console.error("Error refreshing attendance:", error);
      }
    }
  };

  // Handle Clock In
  const handleClockIn = async () => {
    if (!selectedEmployee) return;
    try {
      await clockIn(selectedEmployee);
      // Refresh data
      await refreshAttendance();
    } catch (error) {
      console.error("Clock in failed:", error);
    }
  };

  // Handle Clock Out
  const handleClockOut = async () => {
    if (!selectedEmployee) return;
    try {
      await clockOut(selectedEmployee);
      // Refresh data
      await refreshAttendance();
    } catch (error) {
      console.error("Clock out failed:", error);
    }
  };

  // Handle bulk action submit
  const handleActionSubmit = async () => {
    if (!selectedEmployee || !selectedAction) {
      return;
    }

    try {
      switch (selectedAction) {
        case "clockin": {
          await handleClockIn();
          break;
        }
        case "clockout": {
          await handleClockOut();
          break;
        }
        case "absent": {
          // Mark as absent for today
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          await createManualAttendance({
            employeeId: selectedEmployee,
            date: today.toISOString(),
            status: "Absent",
          });
          await refreshAttendance();
          break;
        }
        case "holiday": {
          // Mark as holiday for today
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          await createManualAttendance({
            employeeId: selectedEmployee,
            date: today.toISOString(),
            status: "Holiday",
          });
          await refreshAttendance();
          break;
        }
        default:
          break;
      }
      // Reset action dropdown
      setSelectedAction("");
    } catch (error) {
      console.error("Action failed:", error);
    }
  };

  // Format time display
  const formatTimeDisplay = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Format date display
  const formatDateDisplay = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Format hours to "Xh Ym" format
  const formatHours = (hours) => {
    if (!hours || hours === 0) return "0h 00m";
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${String(m).padStart(2, "0")}m`;
  };

  // Prepare table data
  const dataSource = useMemo(() => {
    return attendanceData
      .filter((record) => {
        if (employeeFilter === "all") return true;
        return record.employee?.name === employeeFilter;
      })
      .map((record) => ({
        key: record._id,
        employee: record.employee?.name || "N/A",
        date: formatDateDisplay(record.date),
        status: record.status,
        clockin: formatTimeDisplay(record.clockIn),
        clockout: formatTimeDisplay(record.clockOut),
        production: record.productionHours
          ? formatHours(record.productionHours)
          : "-",
        overtime:
          record.overTime !== undefined && record.overTime !== null
            ? formatHours(record.overTime)
            : "0h 00m",
        totalhours: record.totalHours ? formatHours(record.totalHours) : "-",
      }));
  }, [attendanceData, employeeFilter]);

  const columns = [
    {
      title: "Employee",
      dataIndex: "employee",
      sorter: (a, b) => a.employee.localeCompare(b.employee),
    },
    {
      title: "Date",
      dataIndex: "date",
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text) => (
        <span
          className={`badge d-inline-flex align-items-center badge-xs ${
            text === "Present"
              ? "badge-success"
              : text === "Holiday"
              ? "badge-purple"
              : text === "Half Day"
              ? "badge-warning"
              : "badge-danger"
          }`}
        >
          <i className="ti ti-point-filled me-1"></i> {text}
        </span>
      ),
      sorter: (a, b) => a.status.localeCompare(b.status),
    },
    {
      title: "Clock In",
      dataIndex: "clockin",
      sorter: (a, b) => a.clockin.localeCompare(b.clockin),
    },
    {
      title: "Clock Out",
      dataIndex: "clockout",
      sorter: (a, b) => a.clockout.localeCompare(b.clockout),
    },
    {
      title: "Production",
      dataIndex: "production",
      sorter: (a, b) => {
        const aVal = parseFloat(a.production) || 0;
        const bVal = parseFloat(b.production) || 0;
        return aVal - bVal;
      },
    },
    {
      title: "OverTime",
      dataIndex: "overtime",
      sorter: (a, b) => {
        const aVal = parseFloat(a.overtime) || 0;
        const bVal = parseFloat(b.overtime) || 0;
        return aVal - bVal;
      },
    },
    {
      title: "Total Hours",
      dataIndex: "totalhours",
      sorter: (a, b) => {
        const aVal = parseFloat(a.totalhours) || 0;
        const bVal = parseFloat(b.totalhours) || 0;
        return aVal - bVal;
      },
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="attendance-header">
            <div className="attendance-content d-flex align-items-center gap-3">
              <div className="d-flex align-items-center">
                <label className="me-2 mb-0">Employee:</label>
                <select
                  className="form-select"
                  style={{ width: "250px" }}
                  value={selectedEmployee || ""}
                  onChange={handleEmployeeChange}
                  disabled={
                    employeesLoading || !employees || employees.length === 0
                  }
                >
                  {employees &&
                    employees.map((employee) => (
                      <option key={employee._id} value={employee._id}>
                        {employee.name}
                      </option>
                    ))}
                </select>
              </div>

              <div className="d-flex align-items-center">
                <label className="me-2 mb-0">Action:</label>
                <select
                  className="form-select"
                  style={{ width: "180px" }}
                  value={selectedAction}
                  onChange={(e) => setSelectedAction(e.target.value)}
                  disabled={!selectedEmployee || attendanceLoading}
                >
                  <option value="">Select Action</option>
                  {/* Only show options if attendance not already marked as Absent or Holiday */}
                  {todayStatus?.attendance?.status !== "Absent" &&
                    todayStatus?.attendance?.status !== "Holiday" && (
                      <>
                        {/* Show Clock In only if not already clocked in */}
                        {!todayStatus?.hasClockIn && (
                          <option value="clockin">Clock In</option>
                        )}
                        {/* Show Clock Out only if already clocked in */}
                        {todayStatus?.hasClockIn &&
                          !todayStatus?.hasClockOut && (
                            <option value="clockout">Clock Out</option>
                          )}
                        <option value="absent">Mark Absent</option>
                        <option value="holiday">Mark Weekly Off</option>
                      </>
                    )}
                </select>
              </div>

              <button
                className="btn btn-primary"
                onClick={handleActionSubmit}
                disabled={
                  !selectedEmployee || !selectedAction || attendanceLoading
                }
              >
                Submit
              </button>
            </div>
            <div>
              <ul className="table-top-head">
                <TooltipIcons />
                <li>
                  <Link
                    to="#"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title="Refresh"
                    onClick={(e) => {
                      e.preventDefault();
                      refreshAttendance();
                    }}
                  >
                    <i className="ti ti-refresh" />
                  </Link>
                </li>
                <CollapesIcon />
              </ul>
            </div>
          </div>
          <div className="row">
            <div className="col-xl-4 col-lg-12 d-flex">
              <div className="card w-100 border-0">
                <div className="card-body">
                  <h5 className="mb-3 pb-3 border-bottom d-flex justify-content-between align-items-center fs-18">
                    Attendance
                    <span className="text-purple fs-14">
                      {formatDate(currentTime)}
                    </span>
                  </h5>
                  <div className="d-flex align-items-center mb-3">
                    <div className="me-2">
                      <ImageWithBasePath
                        src="./assets/img/icons/time-big.svg"
                        alt="time-img"
                      />
                    </div>
                    <div>
                      <h2>{formatTime(currentTime)}</h2>
                      <p>Current Time</p>
                    </div>
                  </div>
                  <div className="attendance-status-card">
                    {!todayStatus?.hasClockIn ? (
                      <div className="alert alert-info mb-0 text-center">
                        <i className="ti ti-info-circle me-2"></i>
                        Not Clocked In Yet
                      </div>
                    ) : !todayStatus?.hasClockOut ? (
                      <div className="alert alert-warning mb-0 text-center">
                        <i className="ti ti-clock me-2"></i>
                        Currently Clocked In
                      </div>
                    ) : (
                      <div className="alert alert-success mb-0 text-center">
                        <i className="ti ti-check me-2"></i>
                        Shift Completed
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-8 col-lg-12 d-flex">
              <div className="card w-100 border-0">
                <div className="card-body">
                  <h5 className="border-bottom pb-3 mb-3">
                    Days Overview for{" "}
                    {employees?.find((emp) => emp._id === selectedEmployee)
                      ?.name || "Selected Employee"}
                  </h5>
                  <div className="row gy-3">
                    <div className="col-lg-2 col-md-3 col-sm-4 text-center">
                      <div>
                        <span className="d-flex align-items-center justify-content-center avatar avatar-xl bg-primary-transparent fw-bold fs-20 mb-2 mx-auto">
                          {getTotalDaysInMonth(currentTime)}
                        </span>
                        <p className="fs-14">
                          Total <br /> Days
                        </p>
                      </div>
                    </div>
                    <div className="col-lg-2 col-md-3 col-sm-4 text-center">
                      <div>
                        <span className="d-flex align-items-center justify-content-center avatar avatar-xl bg-purple-transparent text-purple fw-bold fs-20 mb-2 mx-auto">
                          {summary?.present || 0}
                        </span>
                        <p className="fs-14">
                          Present <br />
                          Days
                        </p>
                      </div>
                    </div>
                    <div className="col-lg-2 col-md-3 col-sm-4 text-center">
                      <div>
                        <span className="d-flex align-items-center justify-content-center avatar avatar-xl bg-danger-transparent fw-bold fs-20 mb-2 mx-auto">
                          {summary?.absent || 0}
                        </span>
                        <p className="fs-14">
                          Absent <br />
                          Days
                        </p>
                      </div>
                    </div>
                    <div className="col-lg-2 col-md-3 col-sm-4 text-center">
                      <div>
                        <span className="d-flex align-items-center justify-content-center avatar avatar-xl bg-warning-transparent fw-bold fs-20 mb-2 mx-auto">
                          {summary?.halfDays || 0}
                        </span>
                        <p className="fs-14">
                          Half
                          <br /> Days
                        </p>
                      </div>
                    </div>
                    <div className="col-lg-2 col-md-3 col-sm-4 text-center">
                      <div>
                        <span className="d-flex align-items-center justify-content-center avatar avatar-xl bg-cyan-transparent text-cyan fw-bold fs-20 mb-2 mx-auto">
                          {summary?.totalOvertime
                            ? summary.totalOvertime.toFixed(1)
                            : 0}
                        </span>
                        <p className="fs-14">
                          Overtime <br />
                          Hours
                        </p>
                      </div>
                    </div>
                    <div className="col-lg-2 col-md-3 col-sm-4 text-center">
                      <div>
                        <span className="d-flex align-items-center justify-content-center avatar avatar-xl bg-success-transparent text-success fw-bold fs-20 mb-2 mx-auto">
                          {summary?.holidays || 0}
                        </span>
                        <p className="fs-14">
                          Weekly <br /> Off
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* /product list */}
          <div className="card table-list-card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="search-set"></div>
              <div className="d-flex my-xl-auto right-content align-items-center row-gap-3">
                <div className="dropdown me-2">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {employeeFilter === "all"
                      ? "All Employees"
                      : employeeFilter}
                  </Link>
                  <ul className="dropdown-menu dropdown-menu-end p-3">
                    <li key="all">
                      <Link
                        to="#"
                        className="dropdown-item rounded-1"
                        onClick={(e) => {
                          e.preventDefault();
                          setEmployeeFilter("all");
                        }}
                      >
                        All Employees
                      </Link>
                    </li>
                    {employees &&
                      employees.map((emp) => (
                        <li key={emp._id}>
                          <Link
                            to="#"
                            className="dropdown-item rounded-1"
                            onClick={(e) => {
                              e.preventDefault();
                              setEmployeeFilter(emp.name);
                            }}
                          >
                            {emp.name}
                          </Link>
                        </li>
                      ))}
                  </ul>
                </div>
                <PredefinedDateRanges onDateChange={handleDateRangeChange} />
              </div>
            </div>
            <div className="card-body pb-0">
              <div className="table-responsive">
                {attendanceLoading || employeesLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <Table
                    key={`${employeeFilter}-${attendanceData.length}`}
                    columns={columns}
                    dataSource={dataSource}
                    rowKey="key"
                  />
                )}
              </div>
            </div>
          </div>
          {/* /product list */}
        </div>
        <CommonFooter />
      </div>
    </div>
  );
};

export default AttendanceEmployee;
